import type { RequestPriority, RequestStatus } from "@/hooks/types";

/**
 * The `common` bundle key naming each request status.
 *
 * A `Record` over the union rather than a lookup that can miss: a status added
 * to `REQUEST_STATUSES` without a word for it here is a type error, which is
 * the loud failure that keeps the requests filter honest (NAV-47) instead of
 * letting it offer a raw `CANCELLED`. Both bundles are asserted to carry every
 * key in `requests.test.ts` — a missing translation is otherwise invisible,
 * since i18next falls back to the key.
 *
 * Only the filter reads this today. The status *pill* beside each request
 * still renders the bare enum (`RequestStatusPill`'s fallback), as it did for
 * all five statuses before this — localizing those is its own change.
 */
export const REQUEST_STATUS_LABEL: Record<RequestStatus, string> = {
  PENDING: "pending",
  APPROVED: "approved",
  DENIED: "denied",
  CANCELLED: "cancelled",
  EXPIRED: "expired",
};

/** Urgency answers offered by the "How soon do you need it?" selects. */
export type Urgency = "asap" | "week" | "month" | "no-rush";

// Urgency answers map onto the request's priority field, which lands
// on the request document. Shared by the tool-detail modal and the
// request-access wizard form.
export const URGENCY_TO_PRIORITY: Record<Urgency, RequestPriority> = {
  asap: "URGENT",
  week: "HIGH",
  month: "NORMAL",
  "no-rush": "LOW",
};

/** Whether a request counts as urgent on the dashboards (Jira NAV-270): its
 *  requester asked for it as soon as possible. "Within a week" (HIGH) is not
 *  urgent, which is also how the request list's priority filter reads it, so
 *  a tile's urgent count matches what that filter shows. */
export function isUrgent(request: { priority: RequestPriority }): boolean {
  return request.priority === "URGENT";
}

/** The three answers both request forms collect: the broad use-case choice,
 *  the urgency, and the requester's own description of what they intend to
 *  do. The wizard's proposal step and the tool page's access modal keep
 *  exactly this state, so the shape lives once. */
export interface RequestAnswers {
  useCase: string;
  urgency: Urgency | "";
  intendedUse: string;
}

/** The form state before anything is answered — both forms open here, and a
 *  submitted request resets back to it. Fresh each call, so neither form can
 *  hand the other a mutated shared object. */
export function emptyRequestAnswers(): RequestAnswers {
  return { useCase: "", urgency: "", intendedUse: "" };
}

/** A request can only be sent with all three answers in place. The free text
 *  must actually say something — whitespace is no description, and the
 *  Firestore rules refuse an empty one for a proposal.
 *
 *  A type guard, not just a boolean: both submit paths look the urgency up in
 *  `URGENCY_TO_PRIORITY` right after this check passes, so "complete" has to
 *  narrow `urgency` away from its empty-string default to do that safely. */
export function requestAnswersComplete(
  answers: RequestAnswers,
): answers is RequestAnswers & { urgency: Urgency } {
  return Boolean(
    answers.useCase && answers.urgency && answers.intendedUse.trim(),
  );
}

/**
 * Display-only reference number for a request, derived from its document
 * id (NV-year-suffix). Purely cosmetic — never used to look a request up.
 */
export function requestReference(requestId: string): string {
  return `NV-${new Date().getFullYear()}-${requestId.slice(-4).toUpperCase()}`;
}

/**
 * The reason the requester actually gave, or `null` when they gave none.
 *
 * One rule for every surface that shows it, because three surfaces spelled it
 * three ways and agreed only by coincidence (review of #81): the decision card
 * tested truthiness, the BU-admin detail used `??`, and the backend used `or`.
 * Only `??` treats `""` as a reason, so the divergence was one keystroke from
 * rendering an empty row on the one page whose whole job here is to say whether
 * a reason exists.
 *
 * Trims, so whitespace is not a reason either — a hand-written document can
 * carry `"  "` (the rules do not validate this field), and the point of the
 * fallback is defeated by anything that merely looks non-empty.
 *
 * Returns `null` rather than the label, because the words are the caller's:
 * they live in the caller's own i18n namespace. `Request.display_justification`
 * is the backend's half of the same rule, for the mails.
 */
export function givenJustification(
  justification: string | null | undefined,
): string | null {
  return justification?.trim() || null;
}

/**
 * The first of these names that carries text, or `""` when none does.
 *
 * A name a request does not have is stored as `""`, not as null: both create
 * paths write `subject.productName?.trim() ?? ""` and
 * `[first, last].join(" ") || displayName || email || ""`
 * (`useCreateEvaluationRequest.ts`, `useCreateAccessRequest.ts`), and the rules
 * constrain which fields a request may change, never that they carry text. So
 * `??` stops at the blank and a nullish chain renders an empty heading where
 * the fallback label belongs. A dozen surfaces spelled that chain by hand and
 * three of them spelled it a different way, so one and the same nameless
 * request read "Tool name missing" in the BU-admin list and showed a blank
 * heading on the detail page that list links to. Every surface that shows a
 * name of this kind goes through here now, the four dialog titles in
 * `AccessDecisionCard` included: a card headed "A tool" whose confirmation
 * dialog is titled "Approve the request for ?" is the same divergence at the
 * moment somebody commits the decision. One rule, for the same reason
 * `givenJustification` above is one rule.
 *
 * `grep -rn 'Name ??' apps/frontend/src` is where to look for the next one.
 * Most of what it returns is a form value, a filter or a field the writer
 * omits when it is blank, and there `??` is right; what it cannot tell you is
 * which of the rest is a name somebody reads. That is a reading, so this
 * paragraph does not claim a count.
 *
 * Trims, so whitespace is not a name either.
 *
 * The label goes in as the last candidate, so a call site has no fallback
 * operator left to get wrong. The order of the ones before it stays the
 * caller's: an evaluation is read by the product it proposes, an access request
 * by the catalogue tool it asks for, and the surfaces that show both say so
 * themselves.
 */
export function firstNamed(
  ...candidates: Array<string | null | undefined>
): string {
  for (const candidate of candidates) {
    const named = candidate?.trim();
    if (named) return named;
  }
  return "";
}

/**
 * An address, when it adds something to the name beside it.
 *
 * `null` when there is no address **and** when it is the same string as the
 * name. Every writer of a display name in this app falls back to the address
 * when the account carries no first or last name — `User.display_name` on the
 * backend, and `evaluation-steps.lazy.tsx` for a step responsible — so the
 * surfaces that show both would print it twice (PR #138 review).
 *
 * One helper rather than the condition written out per surface, because
 * written out it drifts: the step-responsible line was the copy that did not
 * get it, and printed the address twice for a whole population. No count of
 * the callers here — it has been stated wrongly twice, in both directions
 * (PR #138 review), and a number beside a list it does not compute is only
 * ever as fresh as the last person to edit the list.
 *
 * **Ask it about the string you are actually printing**, not about the field
 * that string came from. Judged against a `displayName` while the caller
 * printed `firstNamed(displayName, email)`, it appends an address to an
 * address — there is no name for the address to be equal to. That is what
 * `stepResponsibleName` exists to make impossible at its call site.
 *
 * Named for a name and an address rather than for the requester, because the
 * requester is only one of the people it is asked about.
 */
export function addressBesideName(
  name: string | null | undefined,
  email: string | null | undefined,
): string | null {
  const address = email?.trim();
  if (!address) return null;
  return address === name?.trim() ? null : address;
}

/**
 * Which sentence an empty request queue should say.
 *
 * **Two facts wore one sentence.** The queue counted what survived its filters,
 * so "No requests found" stood both for a queue nothing has ever reached and
 * for a queue whose filters happen to match nothing. A new BU admin met the
 * first on the day they were given the role, on the page the role is *for*, and
 * a sentence that sounds like a failed search is one they cannot act on.
 *
 * Here rather than in the page because the page needs a router, a data hook and
 * three filters to render at all, and this is the whole of the decision.
 */
export type QueueEmptyReason = "none-yet" | "none-match";

export function queueEmptyReason(arrived: number): QueueEmptyReason {
  return arrived === 0 ? "none-yet" : "none-match";
}
